import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaThLarge, FaSearch, FaHistory, FaHandsHelping, 
  FaBullhorn, FaChartLine, FaCloudSun, FaRobot, 
  FaUser, FaCog, FaSignOutAlt, FaExclamationTriangle, 
  FaCheckCircle, FaTint, FaSeedling, FaRupeeSign, 
  FaBoxOpen, FaArrowRight, FaChevronDown, FaMapMarkerAlt 
} from 'react-icons/fa';
import { 
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, 
  Tooltip, CartesianGrid 
} from 'recharts';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const Dashboard = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'mr';
  const isHindi = currentLang === 'hi';
  const isMarathi = currentLang === 'mr';

  const getText = (hi, mr, en) => {
    if (currentLang === 'mr') return mr || hi;
    if (currentLang === 'hi') return hi;
    if (currentLang === 'gu') return hi;
    return en || hi;
  };

  const [dashboardData, setDashboardData] = useState(null);
  const [selectedCrop, setSelectedCrop] = useState('Banana');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();

    const handleLocationChange = () => {
      fetchDashboardData();
    };

    window.addEventListener('farmer_location_changed', handleLocationChange);
    return () => window.removeEventListener('farmer_location_changed', handleLocationChange);
  }, [selectedCrop, currentLang]);

  const fetchDashboardData = async () => {
    const savedLoc = localStorage.getItem('farmer_location') || 'Jalgaon, Maharashtra';
    try {
      setLoading(true);
      const res = await axios.get(`http://localhost:8000/api/distress-dashboard-mock?crop=${selectedCrop}&lang=${currentLang}`);
      const data = res.data;
      if (savedLoc && data && data.farmer) {
        data.farmer.location = savedLoc;
        data.farmer.location_hi = savedLoc;
        data.farmer.location_mr = savedLoc;
      }
      setDashboardData(data);
      if (data && data.assessment) {
        localStorage.setItem('lastDistressAssessment', JSON.stringify(data.assessment));
      }
    } catch (err) {
      console.warn('Using local mock dashboard state:', err);
      const fallbackData = {
        farmer: {
          name: 'Sanket Bhende',
          name_hi: 'संकेत भेंडे',
          name_mr: 'संकेत भेंडे',
          location: savedLoc,
          location_hi: savedLoc,
          location_mr: savedLoc,
          main_crop: 'Banana',
          crop_stage: 'Fruit Development',
          crop_stage_hi: 'फल/दाना विकास',
          crop_stage_mr: 'फळ विकास / घड भरणे'
        },
        assessment: {
          dri_score: 72,
          level: 'HIGH',
          level_localized: getText('उच्च जोखिम', 'उच्च धोका', 'HIGH RISK'),
          level_class: 'high',
          status_message: getText(
            'आपकी फसल उच्च जोखिम में है। समय रहते कदम उठाएं और नुकसान से बचें।',
            'तुमचे पीक उच्च धोक्यात आहे. वेळेवर पावले उचला आणि नुकसान टाळा.',
            'Your crop is under high distress. Take action now to avoid potential losses.'
          ),
          factors: [
            { id: 'weather', name: getText('मौसम', 'हवामान', 'Weather'), score: 75, level: getText('उच्च', 'उच्च', 'High'), impact_pct: 25, reason_hi: 'बारिश अपेक्षित से कम है', reason_mr: 'अपेक्षित पावसापेक्षा कमी पाऊस', reason_en: 'Rainfall below expected level' },
            { id: 'soil', name: getText('मिट्टी', 'माती', 'Soil'), score: 45, level: getText('मध्यम', 'मध्यम', 'Moderate'), impact_pct: 16, reason_hi: 'मिट्टी में नमी कम है', reason_mr: 'मातीत ओलावा कमी आहे', reason_en: 'Low soil moisture stress' },
            { id: 'water', name: getText('पानी', 'पाणी', 'Water'), score: 70, level: getText('उच्च', 'उच्च', 'High'), impact_pct: 22, reason_hi: 'सिंचाई उपलब्धता सीमित है', reason_mr: 'सिंचनाची उपलब्धता मर्यादित', reason_en: 'Low irrigation availability' },
            { id: 'market', name: getText('मार्केट', 'बाजार', 'Market'), score: 68, level: getText('उच्च', 'उच्च', 'High'), impact_pct: 18, reason_hi: 'कीमत गिर रही है (MSP से कम)', reason_mr: 'भाव घसरत आहेत (MSP पेक्षा कमी)', reason_en: 'Price below MSP & falling trend' },
            { id: 'spoilage', name: getText('पोस्ट-हार्वेस्ट', 'कापणीनंतर', 'Spoilage'), score: 35, level: getText('मध्यम', 'मध्यम', 'Moderate'), impact_pct: 7, reason_hi: 'भंडारण स्थिति मध्यम', reason_mr: 'साठवणूक जोखीम मध्यम', reason_en: 'Moderate storage risk' }
          ]
        },
        trend_7days: [
          { date: '14 May', date_hi: '14 मई', score: 25 },
          { date: '15 May', date_hi: '15 मई', score: 28 },
          { date: '16 May', date_hi: '16 मई', score: 52 },
          { date: '17 May', date_hi: '17 मई', score: 48 },
          { date: '18 May', date_hi: '18 मई', score: 68 },
          { date: '19 May', date_hi: '19 मई', score: 75 },
          { date: '20 May', date_hi: '20 मई', score: 72 }
        ],
        alerts: [
          { id: 1, title_en: 'Rainfall below expected level in your area for next 5 days.', title_hi: 'आपके क्षेत्र में अपेक्षित से कम बारिश की संभावना।', level: 'High', level_hi: 'उच्च प्रभाव' },
          { id: 2, title_en: 'Market price of Wheat is falling in nearby mandis.', title_hi: 'गेहूं का कीमत आपके नजदीकी मंडी में गिर रहा है।', level: 'High', level_hi: 'मध्यम प्रभाव' },
          { id: 3, title_en: 'Soil moisture is low. Irrigation recommended.', title_hi: 'आपके खेत की मिट्टी में नमी कम है। सिंचाई की आवश्यकता हो सकती है।', level: 'Moderate', level_hi: 'उच्च प्रभाव' }
        ]
      };
      setDashboardData(fallbackData);
      localStorage.setItem('lastDistressAssessment', JSON.stringify(fallbackData.assessment));
    } finally {
      setLoading(false);
    }
  };

  const dri = dashboardData?.assessment?.dri_score || 72;
  const factors = dashboardData?.assessment?.factors || [];
  const trendData = dashboardData?.trend_7days || [];
  const alerts = dashboardData?.alerts || [];

  return (
    <div className="fd-cockpit-content">
        {/* Cockpit Top Header Greeting */}
        <div className="fd-cockpit-header">
          <div className="fd-greeting-box">
            <h1 className="fd-welcome-title">
              {getText('नमस्ते', 'नमस्कार', 'Welcome')}, <span className="fd-farmer-highlight">{dashboardData?.farmer?.name || 'Sanket'}</span>
            </h1>
            <p>{getText('आज आपके खेत की स्थिति', 'आज तुमच्या शेताची स्थिती', "Here's your farm situation today.")}</p>
          </div>

          {/* Crop Selector Chip */}
          <div className="fd-crop-stage-chip">
            <div className="fd-crop-chip-icon">🍌</div>
            <div className="fd-crop-chip-text">
              <span className="fd-chip-title">{getText('फसल: केला', 'पीक: केळी (Banana)', 'Crop: Banana')}</span>
              <span className="fd-chip-sub">{getText('अवस्था: फल विकास', 'अवस्था: घड भरणे / फळ विकास', 'Stage: Fruit Development')}</span>
            </div>
            <FaChevronDown className="fd-chip-chevron" />
          </div>
        </div>

        {/* Top 2-Column Row: Overall DRI Speed-Dial Gauge + 7-Day Trend Chart */}
        <div className="fd-dashboard-grid-top">
          {/* Semicircular DRI Speed-Dial Card */}
          <div className="fd-gauge-card fd-card">
            <div className="fd-card-header-simple">
              <h3>{getText('आपका डिस्ट्रेस जोखिम (DRI)', 'तुमचा एकूण संकट धोका (DRI)', 'Overall Distress Risk (DRI)')}</h3>
            </div>

            <div className="fd-gauge-container">
              {/* Semi-circular Speed-Dial SVG */}
              <svg viewBox="0 0 200 115" className="fd-dial-svg">
                <defs>
                  <linearGradient id="dialGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="50%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#ef4444" />
                  </linearGradient>
                </defs>
                {/* Background Arc */}
                <path 
                  d="M 20 100 A 80 80 0 0 1 180 100" 
                  fill="none" 
                  stroke="#e2e8f0" 
                  strokeWidth="16" 
                  strokeLinecap="round" 
                />
                {/* Colored Gradient Value Arc */}
                <path 
                  d="M 20 100 A 80 80 0 0 1 180 100" 
                  fill="none" 
                  stroke="url(#dialGradient)" 
                  strokeWidth="16" 
                  strokeLinecap="round" 
                  strokeDasharray="251.2"
                  strokeDashoffset={251.2 * (1 - (dri / 100))}
                  style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
                />
              </svg>

              {/* Big Center Score & Severity Badge */}
              <div className="fd-gauge-center-val">
                <div className="fd-big-score">
                  <span className="fd-score-number">{dri}</span>
                  <span className="fd-score-den">/100</span>
                </div>
                <div className="fd-badge-high">
                  <FaExclamationTriangle />
                  <span>{getText('उच्च जोखिम', 'उच्च धोका', 'HIGH RISK')}</span>
                </div>
              </div>
            </div>

            <div className="fd-gauge-bottom">
              <p className="fd-gauge-msg">
                {isHindi 
                  ? 'आपकी फसल उच्च जोखिम में है। समय रहते कदम उठाएं और नुकसान से बचें।'
                  : 'Your crop is under high distress. Take action now to avoid potential losses.'}
              </p>
              <Link to="/risk-result" className="fd-view-details-link">
                <span>{isHindi ? 'विवरण देखें' : 'View Details'}</span>
                <FaArrowRight />
              </Link>
            </div>
          </div>

          {/* 7-Day Risk Trend Card */}
          <div className="fd-trend-card fd-card">
            <div className="fd-card-header-flex">
              <h3>{isHindi ? 'रिस्क ट्रेंड (7 दिन)' : 'Risk Trend (7 Days)'}</h3>
              <Link to="/history" className="fd-sublink">
                {isHindi ? 'पूरा इतिहास देखें →' : 'View Full History →'}
              </Link>
            </div>

            <div className="fd-chart-wrapper">
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={trendData} margin={{ top: 15, right: 15, left: -25, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey={isHindi ? "date_hi" : "date"} 
                    tick={{ fontSize: 11, fill: '#64748b' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 11, fill: '#64748b' }} 
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip 
                    contentStyle={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#ef4444" 
                    strokeWidth={2.5} 
                    dot={{ fill: '#ef4444', r: 4 }} 
                    activeDot={{ r: 6 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="fd-trend-caption">
              <span className="fd-dot red"></span>
              <span>{isHindi ? 'पिछले 4 दिनों में जल व मौसमी तनाव से जोखिम बढ़ा है' : 'Rising trend due to water deficit and climate shock'}</span>
            </div>
          </div>
        </div>

        {/* 5 Factor Status Breakdown Row */}
        <div className="fd-factors-status-section fd-card">
          <div className="fd-card-header-flex">
            <h3>{isHindi ? '5 फैक्टर स्थिति' : '5 Factor Status'}</h3>
            <Link to="/risk-result" className="fd-sublink">
              {isHindi ? 'विवरण देखें →' : 'View Details →'}
            </Link>
          </div>

          <div className="fd-factors-5grid">
            {factors.map((f) => (
              <div key={f.id} className={`fd-factor-col ${f.id}`}>
                <div className="fd-factor-icon-wrap">
                  {f.id === 'weather' && '🌦️'}
                  {f.id === 'soil' && '🌱'}
                  {f.id === 'water' && '💧'}
                  {f.id === 'market' && '₹'}
                  {f.id === 'spoilage' && '📦'}
                </div>
                <span className="fd-factor-name">{f.name}</span>
                <span className={`fd-factor-level-badge ${f.level === 'उच्च' || f.level === 'High' ? 'high' : 'mod'}`}>
                  {f.level}
                </span>
                <span className="fd-factor-impact-label">
                  {isHindi ? `प्रभाव: ${f.impact_pct}%` : `Impact: ${f.impact_pct}%`}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Split Row: Important Alerts & Quick Actions */}
        <div className="fd-dashboard-bottom-grid">
          {/* Important Alerts */}
          <div className="fd-alerts-card fd-card">
            <div className="fd-card-header-flex">
              <div className="fd-alert-title-box">
                <FaExclamationTriangle className="fd-alert-hdr-icon red" />
                <h3>{isHindi ? 'महत्वपूर्ण अलर्ट' : 'Important Alerts'}</h3>
              </div>
              <span className="fd-sublink">{isHindi ? 'सभी अलर्ट देखें →' : 'View All →'}</span>
            </div>

            <div className="fd-alerts-list">
              {alerts.map((a) => (
                <div key={a.id} className="fd-alert-item">
                  <div className="fd-alert-icon-col">
                    {a.id === 1 && '🌧️'}
                    {a.id === 2 && '📉'}
                    {a.id === 3 && '🌱'}
                  </div>
                  <div className="fd-alert-text-col">
                    <p>{isHindi ? a.title_hi : a.title_en}</p>
                  </div>
                  <div className="fd-alert-badge-col">
                    <span className={`fd-badge-${a.level === 'High' ? 'high' : 'mod'}`}>
                      {isHindi ? a.level_hi : a.level}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions (तुरंत क्या करें?) */}
          <div className="fd-actions-card fd-card">
            <div className="fd-card-header-flex">
              <div className="fd-alert-title-box">
                <FaCheckCircle className="fd-alert-hdr-icon green" />
                <h3>{isHindi ? 'तुरंत क्या करें?' : 'What To Do Now?'}</h3>
              </div>
            </div>

            <div className="fd-quick-actions-list">
              <div className="fd-qa-item">
                <span className="fd-qa-check">✓</span>
                <p>{isHindi ? 'अगले 1-2 दिनों में हल्की सिंचाई करें' : 'Irrigate your crop within next 1-2 days'}</p>
              </div>
              <div className="fd-qa-item">
                <span className="fd-qa-check">✓</span>
                <p>{isHindi ? 'मौसम अपडेट नियमित रूप से देखें' : 'Monitor upcoming weather before spraying'}</p>
              </div>
              <div className="fd-qa-item">
                <span className="fd-qa-check">✓</span>
                <p>{isHindi ? 'बेहतर कीमत के लिए नजदीकी मंडी विकल्प देखें' : 'Check better market / direct buyer options'}</p>
              </div>
              <div className="fd-qa-item">
                <span className="fd-qa-check">✓</span>
                <p>{isHindi ? 'पोस्ट-हार्वेस्ट स्टोरेज का ध्यान रखें' : 'Maintain proper ventilated storage'}</p>
              </div>
            </div>

            <div className="fd-qa-cta-wrapper">
              <Link to="/interventions" className="fd-btn-primary fd-qa-btn">
                <span>{isHindi ? 'सभी सुझाव देखें →' : 'View All Action Steps →'}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Dashboard;
